/**
 * InvoiceFormView — create / edit form wrapper with seller-details loading states.
 */

import { Button } from '@elsapiens/ui'

import type { Party } from '@/gen/proto'


import { InvoiceForm } from '../../components/billing'
import { downloadInvoicePDF } from '../../services'

import type { ViewMode } from './invoices-helpers'
import type { InvoiceActionsState } from './useInvoiceActions'
import type { Toast } from '../../components/ui'
import type { Invoice } from '../../types/billing'
import type { SellerDetails } from '../../types/seller'

interface Props {
  viewMode: ViewMode
  selectedInvoice: Invoice | null
  cloneSource?: Partial<Invoice> | null
  isLoading: boolean
  isLoadingSellerDetails: boolean
  isVendor: boolean
  sellerDetails: SellerDetails | null
  sellerDetailsError: Error | null
  parties: Party[]
  organizationId: string
  currentLedgerId: string | null
  basePath: string
  searchParams: URLSearchParams
  navigate: (path: string) => void
  addToast: (t: Omit<Toast, 'id'>) => void
  actions: InvoiceActionsState
  onFetchParties: () => Promise<void>
  onRefetchSellerDetails: () => Promise<void>
}

export function InvoiceFormView(props: Props): JSX.Element {
  const {
    viewMode, selectedInvoice, isLoadingSellerDetails, sellerDetails,
    sellerDetailsError, basePath, navigate, onRefetchSellerDetails,
  } = props
  if (sellerDetailsError) {
    return (
      <SellerDetailsError
        basePath={basePath}
        error={sellerDetailsError}
        navigate={navigate}
        onRetry={onRefetchSellerDetails}
      />
    )
  }
  if (isLoadingSellerDetails || !sellerDetails) {
    return <SellerDetailsLoading />
  }
  return <LoadedInvoiceForm {...props} selectedInvoice={selectedInvoice} sellerDetails={sellerDetails} viewMode={viewMode} />
}

function LoadedInvoiceForm({
  viewMode, selectedInvoice, cloneSource, isLoading, isVendor, sellerDetails, parties,
  organizationId, searchParams, addToast, actions, onFetchParties,
}: Props & { sellerDetails: NonNullable<Props['sellerDetails']> }): JSX.Element {
  const initialData = resolveInitialData(selectedInvoice, searchParams, cloneSource)
  const pdfDownloadHandler = viewMode === 'edit' && selectedInvoice
    ? makePdfDownloadHandler(organizationId, selectedInvoice, addToast)
    : undefined
  return (
    <InvoiceForm
      {...(initialData ? { initialData } : {})}
      isLoading={isLoading}
      isVendor={isVendor}
      parties={parties}
      sellerDetails={sellerDetails}
      onCancel={actions.handleBack}
      onDownloadPDF={pdfDownloadHandler}
      onPartyUpdate={() => { void onFetchParties() }}
      onSubmit={actions.handleSubmit}
      onView={
        viewMode === 'edit' && selectedInvoice
          ? () => actions.handleView(selectedInvoice)
          : undefined
      }
    />
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveInitialData(
  selectedInvoice: Invoice | null,
  searchParams: URLSearchParams,
  cloneSource?: Partial<Invoice> | null,
): Partial<Invoice> | undefined {
  if (selectedInvoice) { return selectedInvoice }
  // Cloning seeds a brand-new draft from a copy of an existing invoice.
  if (cloneSource) { return cloneSource }
  const partyId = searchParams.get('partyId')
  if (partyId) { return { partyId } }
  return undefined
}

function makePdfDownloadHandler(
  organizationId: string,
  invoice: Invoice,
  addToast: (t: Omit<Toast, 'id'>) => void,
): () => Promise<void> {
  return async () => {
    try {
      await downloadInvoicePDF(organizationId, invoice.id, invoice.invoiceNumber)
      addToast({ variant: 'success', message: 'Invoice PDF downloaded successfully' })
    } catch {
      addToast({ variant: 'error', message: 'Failed to download invoice PDF' })
    }
  }
}

// ── Loading / error states ────────────────────────────────────────────────────

interface SellerDetailsErrorProps {
  error: Error
  basePath: string
  navigate: (path: string) => void
  onRetry: () => Promise<void>
}

function SellerDetailsError({ error, basePath, navigate, onRetry }: SellerDetailsErrorProps): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="max-w-md w-full bg-surface rounded-lg shadow-lg el-p-lg">
        <div className="flex justify-center el-mb-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
        </div>
        <h2 className="text-xl font-bold text-foreground text-center el-mb-xs">Failed to Load Seller Details</h2>
        <p className="text-foreground-secondary text-center el-mb-md">{error.message}</p>
        <div className="flex el-gap-control-md justify-center">
          <Button variant="secondary" onClick={() => navigate(basePath)}>Back to List</Button>
          <Button onClick={() => { void onRetry() }}>Retry</Button>
        </div>
      </div>
    </div>
  )
}

function SellerDetailsLoading(): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto el-mb-sm" />
        <p className="text-foreground-secondary">Loading seller details...</p>
      </div>
    </div>
  )
}
